import { useEffect, useState } from "react";
import { Download, X, Smartphone, Share2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const DISMISS_KEY = "klrn_pwa_install_dismissed_v2";

/**
 * Floating "Add to Home Screen" prompt.
 *
 * Three paths:
 *   1. Android / desktop Chrome: catches `beforeinstallprompt` → calls
 *      `prompt()` on tap. Works.
 *   2. iOS Safari: no programmatic install. We open a full-screen
 *      instructions overlay showing the user exactly which icon to tap.
 *   3. Already-installed PWAs (display-mode: standalone): nothing shows.
 *
 * 30-day dismissal cooldown.
 */
export default function PWAInstallPrompt() {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosTip, setIosTip] = useState(false);
  const [iosFullInstructions, setIosFullInstructions] = useState(false);

  useEffect(() => {
    try {
      const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (dismissedAt && Date.now() - dismissedAt < 30 * 24 * 60 * 60 * 1000) return;
    } catch {}

    if (window.matchMedia?.("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return;

    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/.test(ua);

    if (isIOS) {
      const t = setTimeout(() => setIosTip(true), 30000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 5000);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Android fallback: if `beforeinstallprompt` doesn't fire within 15s
    // (some Android browsers don't fire it, or it's already been used),
    // show the prompt anyway with a manual instructions path.
    let manualTimer;
    if (isAndroid) {
      manualTimer = setTimeout(() => {
        setShow((v) => v || true);
      }, 15000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (manualTimer) clearTimeout(manualTimer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
    setIosTip(false);
    setIosFullInstructions(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } catch {}
      setDeferredPrompt(null);
      setShow(false);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
      return;
    }
    // No deferred prompt — Android with manual install path. Open the
    // generic "tap the ⋮ menu, then Install app" instructions.
    setIosFullInstructions(true);
  };

  const showIOSInstructions = () => {
    setIosFullInstructions(true);
  };

  // Full-screen iOS / fallback instructions overlay
  if (iosFullInstructions) {
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    return (
      <div
        className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
        data-testid="pwa-install-instructions"
        onClick={dismiss}
      >
        <div
          className="bg-zinc-900 border border-amber-500/40 rounded-2xl max-w-sm w-full p-6 shadow-2xl shadow-amber-500/20"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-zinc-900" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {isFr ? "Ajouter à l'écran d'accueil" : "Add to Home Screen"}
                </h3>
                <p className="text-zinc-400 text-xs">
                  {isFr ? "3 étapes simples" : "3 quick steps"}
                </p>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="text-zinc-500 hover:text-white"
              data-testid="pwa-install-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {isIOS ? (
            <ol className="space-y-3 text-sm text-white">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                <span>
                  {isFr ? "Touchez l'icône " : "Tap the "}
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 mx-1">
                    <Share2 className="w-3 h-3 text-blue-400" />
                    <span className="text-blue-400 text-xs font-medium">{isFr ? "Partager" : "Share"}</span>
                  </span>
                  {isFr ? " en bas de Safari." : " icon at the bottom of Safari."}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                <span>
                  {isFr ? 'Faites défiler et touchez "Sur l\'écran d\'accueil".' : 'Scroll and tap "Add to Home Screen".'}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                <span>
                  {isFr ? 'Touchez "Ajouter" — l\'icône Karine apparaîtra sur votre écran d\'accueil.' : 'Tap "Add" — the Karine icon will appear on your home screen.'}
                </span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm text-white">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                <span>{isFr ? "Touchez le menu ⋮ en haut à droite." : "Tap the ⋮ menu in the top right."}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                <span>{isFr ? 'Touchez "Installer l\'app" ou "Ajouter à l\'écran d\'accueil".' : 'Tap "Install app" or "Add to Home Screen".'}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                <span>{isFr ? 'Touchez "Installer" pour confirmer.' : 'Tap "Install" to confirm.'}</span>
              </li>
            </ol>
          )}

          <div className="mt-5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <p className="text-amber-200 text-xs leading-snug">
              {isFr
                ? "✨ Une fois installée, l'application Karine Lauzon ouvrira en plein écran avec votre logo."
                : "✨ Once installed, the Karine Lauzon app opens fullscreen with your logo on the home screen."}
            </p>
          </div>

          <button
            onClick={dismiss}
            className="mt-4 w-full py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium transition-colors"
            data-testid="pwa-install-got-it"
          >
            {isFr ? "Compris" : "Got it"}
          </button>
        </div>
      </div>
    );
  }

  if (!show && !iosTip) return null;

  return (
    <div
      className="fixed bottom-20 sm:bottom-6 left-3 right-3 sm:left-auto sm:right-6 sm:max-w-sm z-[150] animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="pwa-install-prompt"
    >
      <div className="relative bg-zinc-900 border border-amber-500/40 rounded-2xl shadow-2xl shadow-amber-500/20 p-4 backdrop-blur-md">
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-zinc-500 hover:text-zinc-200 transition-colors"
          aria-label="Dismiss"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-zinc-900" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-white text-sm font-semibold mb-1">
              {isFr ? "Ajouter à l'écran d'accueil" : "Add to Home Screen"}
            </h4>
            <p className="text-zinc-400 text-xs leading-snug mb-3">
              {isFr
                ? "Réservez en un seul appui, recevez vos rappels et accédez à Karine sans le navigateur."
                : "One-tap booking, push reminders, and a faster app-like experience."}
            </p>
            <button
              onClick={iosTip ? showIOSInstructions : install}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-900 text-xs font-semibold transition-all"
              data-testid="pwa-install-btn"
            >
              <Download className="w-3.5 h-3.5" />
              {iosTip ? (isFr ? "Comment installer" : "Show me how") : (isFr ? "Installer" : "Install")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
